import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Settings, RefreshCw, Loader2, Save, DollarSign, Wifi, WifiOff, Wallet, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_ICONS = {
  cash: '💵',
  efectivo: '💶',
  zelle: '⚡',
  mlc: '🏦',
  moneda_nacional: '🇨🇺',
  tropical: '🌴',
};

const AdminB2BFeesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [feeTable, setFeeTable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [expandedChannel, setExpandedChannel] = useState(null);

  const loadFeeTable = async () => {
    try {
      const res = await axios.get(`${API}/admin/b2b/fee-table`, { headers: getAuthHeaders() });
      setFeeTable(res.data);
    } catch (e) {
      toast.error('Error al cargar tabla de fees');
    } finally { setLoading(false); }
  };

  const loadApiStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await axios.get(`${API}/admin/b2b/status`, { headers: getAuthHeaders() });
      setApiStatus(res.data);
    } catch (e) {
      setApiStatus({ health: { status: 'error' }, wallet: { error: 'No conectado' } });
    } finally { setStatusLoading(false); }
  };

  useEffect(() => { loadFeeTable(); loadApiStatus(); }, []);

  const handleFeeChange = (idx, field, value) => {
    const updated = [...feeTable];
    updated[idx] = { ...updated[idx], [field]: value };
    setFeeTable(updated);
  };

  const saveFeeConfig = async (idx) => {
    const cfg = feeTable[idx];
    setSaving(prev => ({ ...prev, [cfg.channel]: true }));
    try {
      await axios.put(`${API}/admin/b2b/fee-table/${cfg.channel}`, {
        channel: cfg.channel,
        fee_gestor: parseFloat(cfg.fee_gestor) || 0,
        fee_cliente: parseFloat(cfg.fee_cliente) || 0,
        fee_type: cfg.fee_type || 'fixed',
        min_amount_gestor: parseFloat(cfg.min_amount_gestor) || 0,
        max_amount_gestor: parseFloat(cfg.max_amount_gestor) || 99999,
        min_amount_cliente: parseFloat(cfg.min_amount_cliente) || 0,
        max_amount_cliente: parseFloat(cfg.max_amount_cliente) || 99999,
        is_active: cfg.is_active !== false,
        display_name: cfg.display_name,
      }, { headers: getAuthHeaders() });
      toast.success(`Canal ${cfg.display_name} actualizado`);
    } catch (e) {
      toast.error('Error al guardar');
    } finally { setSaving(prev => ({ ...prev, [cfg.channel]: false })); }
  };

  const syncApiFees = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/b2b/sync-api-fees`, {}, { headers: getAuthHeaders() });
      toast.success('Fees sincronizados con la API');
      loadFeeTable();
    } catch (e) {
      toast.error('Error al sincronizar: ' + (e.response?.data?.detail || e.message));
    } finally { setSyncing(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="b2b-fees-loading">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  const walletData = apiStatus?.wallet;
  const healthOk = apiStatus?.health?.status === 'ok';

  return (
    <div className="space-y-6" data-testid="admin-b2b-fees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">API B2B - Tarifas</h1>
          <p className="text-sm text-slate-500 mt-1">Configura los fees y rangos por canal para gestores y clientes</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={syncApiFees} disabled={syncing} variant="outline" size="sm" data-testid="sync-fees-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Sincronizar Fees API
          </Button>
        </div>
      </div>

      {/* API Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4" data-testid="api-health-card">
          <div className="flex items-center gap-3">
            {healthOk ? <Wifi className="w-5 h-5 text-emerald-500" /> : <WifiOff className="w-5 h-5 text-red-500" />}
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Estado API</p>
              <p className={`text-sm font-bold ${healthOk ? 'text-emerald-600' : 'text-red-600'}`}>
                {statusLoading ? 'Verificando...' : healthOk ? 'Conectada' : 'Desconectada'}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-testid="api-wallet-card">
          <div className="flex items-center gap-3">
            <Wallet className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Balance API Wallet</p>
              <p className="text-sm font-bold text-slate-800">
                {walletData?.wallet?.available_balance != null
                  ? `$${Number(walletData.wallet.available_balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                  : walletData?.error ? 'N/A' : 'Cargando...'}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-testid="api-channels-card">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-amber-500" />
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Canales Activos</p>
              <p className="text-sm font-bold text-slate-800">
                {feeTable.filter(f => f.is_active !== false).length} / {feeTable.length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Fee Table */}
      <Card className="overflow-hidden" data-testid="fee-table-card">
        <div className="bg-slate-800 text-white px-6 py-3">
          <h2 className="font-semibold flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Tabla de Tarifas por Canal
          </h2>
        </div>

        {/* Desktop table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Canal</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Tipo Fee</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Fee Gestores</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Fee Clientes</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Rango Gestores</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Rango Clientes</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Activo</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Accion</th>
              </tr>
            </thead>
            <tbody>
              {feeTable.map((cfg, idx) => (
                <tr key={cfg.channel} className="border-b hover:bg-slate-50/50" data-testid={`fee-row-${cfg.channel}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{CHANNEL_ICONS[cfg.channel] || '📦'}</span>
                      <div>
                        <p className="font-medium text-slate-800">{cfg.display_name}</p>
                        <p className="text-xs text-slate-400">{cfg.channel}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={cfg.fee_type || 'fixed'}
                      onChange={e => handleFeeChange(idx, 'fee_type', e.target.value)}
                      className="border rounded px-2 py-1 text-sm bg-white"
                      data-testid={`fee-type-${cfg.channel}`}
                    >
                      <option value="fixed">Fijo ($)</option>
                      <option value="percentage">Porcentaje (%)</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      type="number" step="0.01" min="0"
                      value={cfg.fee_gestor}
                      onChange={e => handleFeeChange(idx, 'fee_gestor', e.target.value)}
                      className="w-24 text-center mx-auto"
                      data-testid={`fee-gestor-${cfg.channel}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      type="number" step="0.01" min="0"
                      value={cfg.fee_cliente}
                      onChange={e => handleFeeChange(idx, 'fee_cliente', e.target.value)}
                      className="w-24 text-center mx-auto"
                      data-testid={`fee-cliente-${cfg.channel}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-center">
                      <Input
                        type="number" step="1" min="0"
                        value={cfg.min_amount_gestor}
                        onChange={e => handleFeeChange(idx, 'min_amount_gestor', e.target.value)}
                        className="w-20 text-center text-xs"
                        data-testid={`min-gestor-${cfg.channel}`}
                      />
                      <span className="text-slate-400 text-xs">-</span>
                      <Input
                        type="number" step="1" min="0"
                        value={cfg.max_amount_gestor}
                        onChange={e => handleFeeChange(idx, 'max_amount_gestor', e.target.value)}
                        className="w-20 text-center text-xs"
                        data-testid={`max-gestor-${cfg.channel}`}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-center">
                      <Input
                        type="number" step="1" min="0"
                        value={cfg.min_amount_cliente}
                        onChange={e => handleFeeChange(idx, 'min_amount_cliente', e.target.value)}
                        className="w-20 text-center text-xs"
                        data-testid={`min-cliente-${cfg.channel}`}
                      />
                      <span className="text-slate-400 text-xs">-</span>
                      <Input
                        type="number" step="1" min="0"
                        value={cfg.max_amount_cliente}
                        onChange={e => handleFeeChange(idx, 'max_amount_cliente', e.target.value)}
                        className="w-20 text-center text-xs"
                        data-testid={`max-cliente-${cfg.channel}`}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleFeeChange(idx, 'is_active', !cfg.is_active)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${cfg.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                      data-testid={`toggle-active-${cfg.channel}`}
                    >
                      {cfg.is_active !== false ? 'SI' : 'NO'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Button
                      size="sm"
                      onClick={() => saveFeeConfig(idx)}
                      disabled={saving[cfg.channel]}
                      data-testid={`save-fee-${cfg.channel}`}
                    >
                      {saving[cfg.channel] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="lg:hidden divide-y">
          {feeTable.map((cfg, idx) => (
            <div key={cfg.channel} className="p-4" data-testid={`fee-card-mobile-${cfg.channel}`}>
              <button
                className="w-full flex items-center justify-between"
                onClick={() => setExpandedChannel(expandedChannel === cfg.channel ? null : cfg.channel)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">{CHANNEL_ICONS[cfg.channel] || '📦'}</span>
                  <span className="font-medium text-slate-800">{cfg.display_name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${cfg.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {cfg.is_active !== false ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                {expandedChannel === cfg.channel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {expandedChannel === cfg.channel && (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Tipo Fee</Label>
                      <select
                        value={cfg.fee_type || 'fixed'}
                        onChange={e => handleFeeChange(idx, 'fee_type', e.target.value)}
                        className="w-full border rounded px-2 py-1.5 text-sm bg-white mt-1"
                      >
                        <option value="fixed">Fijo ($)</option>
                        <option value="percentage">Porcentaje (%)</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Activo</Label>
                      <button
                        onClick={() => handleFeeChange(idx, 'is_active', !cfg.is_active)}
                        className={`w-full mt-1 px-3 py-1.5 rounded text-sm font-medium ${cfg.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                      >
                        {cfg.is_active !== false ? 'Activo' : 'Inactivo'}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Fee Gestores</Label>
                      <Input type="number" step="0.01" value={cfg.fee_gestor} onChange={e => handleFeeChange(idx, 'fee_gestor', e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Fee Clientes</Label>
                      <Input type="number" step="0.01" value={cfg.fee_cliente} onChange={e => handleFeeChange(idx, 'fee_cliente', e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Rango Gestores (Min-Max)</Label>
                      <div className="flex gap-1 mt-1">
                        <Input type="number" value={cfg.min_amount_gestor} onChange={e => handleFeeChange(idx, 'min_amount_gestor', e.target.value)} className="text-xs" />
                        <Input type="number" value={cfg.max_amount_gestor} onChange={e => handleFeeChange(idx, 'max_amount_gestor', e.target.value)} className="text-xs" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Rango Clientes (Min-Max)</Label>
                      <div className="flex gap-1 mt-1">
                        <Input type="number" value={cfg.min_amount_cliente} onChange={e => handleFeeChange(idx, 'min_amount_cliente', e.target.value)} className="text-xs" />
                        <Input type="number" value={cfg.max_amount_cliente} onChange={e => handleFeeChange(idx, 'max_amount_cliente', e.target.value)} className="text-xs" />
                      </div>
                    </div>
                  </div>
                  <Button className="w-full" onClick={() => saveFeeConfig(idx)} disabled={saving[cfg.channel]}>
                    {saving[cfg.channel] ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default AdminB2BFeesPage;
